<template>
    <div class="relative">
      <div 
        @click="showCalendar = true"
        class="w-full h-12 px-3 py-2 text-sm border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:border-green-600 flex items-center justify-between cursor-pointer"
        :class="{ 'border-green-600': showCalendar }"
      >
        <div class="flex items-center gap-2">
          <span class="text-gray-500 text-xs absolute bg-white px-2 -top-2 left-2">{{ label }}</span>
          <span :class="{ 'text-gray-400': !modelValue }">
            {{ formattedDate }}
          </span>
        </div>
        <CalendarIcon class="h-5 w-5 text-gray-400" />
      </div>
  
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-1 opacity-0"
      >
        <div 
          v-if="showCalendar" 
          class="absolute z-50 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 p-4"
          style="min-width: 320px"
        >
          <div class="flex justify-between items-center mb-4">
            <button
            type="button" 
              @click="previousMonth" 
              class="focus:outline-none p-1 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeftIcon class="h-5 w-5 text-gray-500" />
            </button>
            
            <div class="flex gap-4">
              <button
              type="button" 
                @click="showYearSelect = !showYearSelect"
                class="focus:outline-none px-4 py-1 text-sm hover:bg-gray-100 rounded-full flex items-center gap-1"
              >
                {{ currentYear }}
                <ChevronDownIcon v-if="!showYearSelect" class="h-4 w-4" />
                <ChevronUpIcon v-else class="h-4 w-4" />
              </button>
              <button
              type="button" 
                @click="showMonthSelect = !showMonthSelect"
                class="focus:outline-none px-4 py-1 text-sm hover:bg-gray-100 rounded-full flex items-center gap-1"
              >
                {{ currentMonthName }}
                <ChevronDownIcon v-if="!showMonthSelect" class="h-4 w-4" />
                <ChevronUpIcon v-else class="h-4 w-4" />
              </button>
            </div>
  
            <button
            type="button" 
              @click="nextMonth" 
              class="focus:outline-none p-1 hover:bg-gray-100 rounded-full"
            >
              <ChevronRightIcon class="h-5 w-5 text-gray-500" />
            </button>
          </div>
  
          <!-- Year Select Dropdown -->
          <div 
            v-if="showYearSelect"
            class="absolute top-16 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          >
            <button
            type="button"
              v-for="year in yearRange"
              :key="year"
              @click="selectYear(year)"
              class="focus:outline-none block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 rounded-lg"
              :class="{ 'bg-[#D9EBCD] text-primary': year === currentYear }"
            >
              {{ year }}
            </button>
          </div>
  
          <!-- Month Select Dropdown -->
          <div 
            v-if="showMonthSelect"
            class="absolute top-16 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          >
            <button
            type="button"
              v-for="(month, index) in months"
              :key="month"
              @click="selectMonth(index)"
              class="focus:outline-none block w-full px-4 py-2 text-sm text-left hover:bg-gray-100 rounded-lg"
              :class="{ 'bg-green-50 text-green-600': index === currentMonth }"
            >
              {{ month }}
            </button>
          </div>
  
          <!-- Calendar Grid -->
          <div class="grid grid-cols-7 gap-1">
            <!-- Day Headers -->
            <div 
              v-for="day in weekDays" 
              :key="day"
              class="text-center text-xs text-gray-500 py-2"
            >
              {{ day }}
            </div>
  
            <!-- Empty cells for previous month -->
            <div
              v-for="n in firstDayOfMonth"
              :key="'empty-' + n"
              class="p-2"
            ></div>
  
            <!-- Date cells -->
            <button
            type="button"
              v-for="date in daysInMonth"
              :key="date"
              @click="selectDate(date)"
              class="focus:outline-none p-2 text-sm rounded-lg hover:bg-gray-100 text-center"
              :class="{
                'bg-green-50 text-green-600': isSelectedDate(date),
                'text-gray-400': isDateDisabled(date)
              }"
              :disabled="isDateDisabled(date)"
            >
              {{ date }}
            </button>
          </div>
        </div>
      </Transition>
  
      <!-- Backdrop -->
      <div 
        v-if="showCalendar"
        class="fixed inset-0 z-40"
        @click="showCalendar = false"
      ></div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue'
  import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-vue-next'
  
  const props = defineProps({
    modelValue: String,
    label: String,
    minDate: String,
    maxDate: String
  })
  
  const emit = defineEmits(['update:modelValue'])
  
  const showCalendar = ref(false)
  const showYearSelect = ref(false)
  const showMonthSelect = ref(false)
  const currentDate = ref(props.modelValue ? new Date(props.modelValue) : new Date())
  
  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
  const months = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
  ]
  
  // Computed properties
  const currentYear = computed(() => currentDate.value.getFullYear())
  const currentMonth = computed(() => currentDate.value.getMonth())
  const currentMonthName = computed(() => months[currentMonth.value])
  
  const yearRange = computed(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 100 }, (_, i) => currentYear - 5 + i)
  })
  
  const firstDayOfMonth = computed(() => {
    const firstDay = new Date(currentYear.value, currentMonth.value, 1).getDay()
    return firstDay
  })
  
  const daysInMonth = computed(() => {
    return new Date(currentYear.value, currentMonth.value + 1, 0).getDate()
  })
  
  const formattedDate = computed(() => {
    if (!props.modelValue) return 'Select date'
    const date = new Date(props.modelValue)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })
  })
  
  // Methods
  const previousMonth = () => {
    currentDate.value = new Date(currentYear.value, currentMonth.value - 1)
  }
  
  const nextMonth = () => {
    currentDate.value = new Date(currentYear.value, currentMonth.value + 1)
  }
  
  const selectYear = (year) => {
    currentDate.value = new Date(year, currentMonth.value)
    showYearSelect.value = false
  }
  
  const selectMonth = (month) => {
    currentDate.value = new Date(currentYear.value, month)
    showMonthSelect.value = false
  }
  
  const selectDate = (date) => {
    const selectedDate = new Date(Date.UTC(currentYear.value, currentMonth.value, date))
    emit('update:modelValue', selectedDate.toISOString().split('T')[0])
    showCalendar.value = false
  }
  
  const isSelectedDate = (date) => {
    if (!props.modelValue) return false
    const selected = new Date(props.modelValue)
    return selected.getDate() === date &&
           selected.getMonth() === currentMonth.value &&
           selected.getFullYear() === currentYear.value
  }
  
  const isDateDisabled = (date) => {
    const currentDate = new Date(currentYear.value, currentMonth.value, date)
    if (props.minDate && currentDate < new Date(props.minDate)) return true
    if (props.maxDate && currentDate > new Date(props.maxDate)) return true
    return false
  }
  </script>
  
  <style scoped>
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  </style>
  
  